import type { OperatorConfig, ValidationResult } from './types';

/**
 * Validates operator configuration
 * Checks for required fields, proper formats, and accessible endpoints
 */
export function validateConfig(config: OperatorConfig): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Validate required string fields
    const requiredStrings: Array<keyof OperatorConfig> = [
        'operatorName',
        'operatorSalt',
        'rpcBaseUrl',
        'contractName',
        'registryContractName',
        'backedToken',
        'appCheckRecaptchaKey',
        'frontendFunctionsBaseUrl',
        'functionsBaseUrl',
        'cloudRunBaseUrl',
    ];

    for (const field of requiredStrings) {
        if (!config[field] || typeof config[field] !== 'string') {
            errors.push(`Missing or invalid required field: ${field}`);
        }
    }

    // Validate Firebase configuration
    if (!config.firebaseConfig) {
        errors.push('Missing firebaseConfig');
    } else {
        const fbConfig = config.firebaseConfig;
        const requiredFbFields: Array<keyof typeof fbConfig> = ['apiKey', 'authDomain', 'databaseURL', 'projectId', 'storageBucket', 'messagingSenderId', 'appId'];

        for (const field of requiredFbFields) {
            if (!fbConfig[field]) {
                errors.push(`Missing Firebase config field: ${field}`);
            }
        }

        // Validate Firebase config format
        if (fbConfig.projectId && !fbConfig.storageBucket?.includes(fbConfig.projectId)) {
            warnings.push('Firebase storageBucket does not match projectId pattern');
        }
    }

    // Validate URLs
    const urlFields: Array<keyof OperatorConfig> = [
        'rpcBaseUrl',
        'frontendFunctionsBaseUrl',
        'functionsBaseUrl',
        'cloudRunBaseUrl',
        'moonpayWidgetBaseUrl',
        'transakWidgetBaseUrl',
    ];

    for (const field of urlFields) {
        const url = config[field] as string;
        if (url && !isValidUrl(url)) {
            errors.push(`Invalid URL format for ${field}: ${url}`);
        }
    }

    // Validate API keys
    if (!config.moonpayApiKey) {
        errors.push('Missing MoonPay API key');
    }
    if (!config.transakApiKey) {
        errors.push('Missing Transak API key');
    }
    if (!config.googleProviderClientId) {
        errors.push('Missing Google Provider Client ID');
    }

    // Validate fiatInProviders array
    if (!Array.isArray(config.fiatInProviders) || config.fiatInProviders.length === 0) {
        errors.push('fiatInProviders must be a non-empty array');
    }

    // Validate upvote config
    if (!config.upvote?.contractName) {
        errors.push('Missing upvote contract name');
    }

    // Validate mobile app config
    if (config.mobileApp) {
        const mobileFields: Array<keyof typeof config.mobileApp> = ['apn', 'dynamicLink', 'announcementUrl', 'isi', 'ibi'];

        for (const field of mobileFields) {
            if (!config.mobileApp[field]) {
                warnings.push(`Missing mobile app field: ${field}`);
            }
        }
    } else {
        warnings.push('Missing mobile app configuration');
    }

    // Validate CDN config
    if (!config.cdn?.storageBaseUrl) {
        warnings.push('Missing CDN storage base URL');
    }

    // Environment-specific validations
    if (config.operatorName === 'eosnation') {
        // Production checks
        if (config.moonpayApiKey?.includes('test')) {
            errors.push('Production environment using test MoonPay API key!');
        }
        if (config.transakWidgetBaseUrl?.includes('staging')) {
            errors.push('Production environment using staging Transak widget!');
        }
        if (config.rpcBaseUrl?.includes('kylin') || config.rpcBaseUrl?.includes('jungle')) {
            errors.push('Production environment using testnet RPC endpoint!');
        }
    }

    return {
        valid: errors.length === 0,
        errors,
        warnings,
    };
}

/**
 * Validates URL format
 */
function isValidUrl(urlString: string): boolean {
    try {
        const url = new URL(urlString);
        return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
        return false;
    }
}

/**
 * Validates configuration and throws error if invalid
 * Use this for critical validation during app initialization
 */
export function validateConfigOrThrow(config: OperatorConfig): void {
    const result = validateConfig(config);

    if (!result.valid) {
        const errorMessage = ['Configuration validation failed:', ...result.errors.map(err => `  ❌ ${err}`)].join('\n');
        throw new Error(errorMessage);
    }

    // Log warnings but don't throw
    if (result.warnings.length > 0) {
        console.warn('Configuration warnings:');
        for (const warning of result.warnings) {
            console.warn(`  ⚠️  ${warning}`);
        }
    }
}

/**
 * Async validation that can check endpoint availability
 * Optional - for use in config validation scripts
 */
export async function validateConfigWithConnectivity(config: OperatorConfig): Promise<ValidationResult> {
    const result = validateConfig(config);

    // Test RPC endpoint connectivity
    try {
        const rpcResponse = await fetch(`${config.rpcBaseUrl}/v1/chain/get_info`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
        });

        if (!rpcResponse.ok) {
            result.warnings.push(`RPC endpoint returned status ${rpcResponse.status}`);
        }
    } catch (error) {
        result.warnings.push(`Unable to connect to RPC endpoint: ${error instanceof Error ? error.message : String(error)}`);
    }

    // Test Firebase endpoints
    try {
        const firebaseResponse = await fetch(`https://${config.firebaseConfig.authDomain}`);
        if (!firebaseResponse.ok && firebaseResponse.status !== 404) {
            result.warnings.push('Firebase auth domain may not be accessible');
        }
    } catch (error) {
        result.warnings.push(`Unable to verify Firebase configuration: ${error instanceof Error ? error.message : String(error)}`);
    }

    return result;
}
